import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useCvStepNavigation } from "../../contexts/CvStepNavigationContext";
import CvSectionHeader from "../Cv/CvSectionHeader";

const tooltipStyles = {
  tooltip: {
    sx: {
      backgroundColor: "#121212",
      color: "#FFFFFF",
      fontSize: "14px",
      lineHeight: "20px",
      fontFamily: "Inter, sans-serif",
      fontWeight: 400,
      px: 1.5,
      py: 1,
      borderRadius: "8px",
      maxWidth: 320,
    },
  },
  arrow: {
    sx: {
      color: "#121212",
    },
  },
};

/* ─── Layout shells ──────────────────────────────────────────── */

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  minHeight: `calc(100vh - ${theme.layout.navbarHeight}px - ${theme.layout.headerHeight}px)`,
  alignItems: "flex-start",
}));

const SidePanel = styled("div")(({ theme }) => ({
  width: theme.layout.sidebarWidth,
  flexShrink: 0,
  backgroundColor: theme.palette.background.neutral,
  padding: theme.spacing(3, 1.5),
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  position: "sticky",
  top: 0,
  alignSelf: "flex-start",
  height: `calc(100vh - ${theme.layout.navbarHeight}px - ${theme.layout.headerHeight}px)`,
}));

const ContentContainer = styled("div")(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  backgroundColor: theme.palette.background.subtle,
}));

const ContentBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  width: "100%",
  display: "flex",
  flexDirection: "column",
}));

/* ─── Step row ───────────────────────────────────────────────── */

const StepRow = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 0.5),
  cursor: "pointer",
  "&:hover .step-label": {
    opacity: 0.75,
  },
}));

const StepTextBlock = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  textAlign: "right",
  flex: 1,
  paddingTop: theme.spacing(1),
  paddingRight: theme.spacing(1.5),
}));

const StepLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  ...theme.typography.cvSectionLabel,
  fontWeight: active ? 600 : 500,
  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  whiteSpace: "nowrap",
}));

const StepHelperText = styled(Typography)(({ theme }) => ({
  ...theme.typography.sm,
  color: theme.palette.text.muted,
  whiteSpace: "nowrap",
}));

const CircleColumn = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flexShrink: 0,
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
}));

const StepIconCircle = styled("div", {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "completed",
})(({ theme, active, completed }) => ({
  width: 36,
  height: 36,
  borderRadius: "50%",
  backgroundColor:
    active || completed ? theme.palette.primary.main : theme.palette.grey[200],
  color:
    active || completed ? theme.palette.common.white : theme.palette.grey[500],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background-color 0.2s",
  "& svg": { fontSize: 18 },
}));

const ConnectorLine = styled("div", {
  shouldForwardProp: (prop) => prop !== "lit",
})(({ theme, lit }) => ({
  width: "2px",
  height: "20px",
  backgroundColor: lit ? theme.palette.primary.main : theme.palette.grey[500],
  opacity: lit ? 1 : 0.2,
  transition: "background-color 0.2s, opacity 0.2s",
}));

/* ─── Component ──────────────────────────────────────────────── */

const CvBuilderMenu = ({ steps }) => {
  const { activeStep, setActiveStep, visitedSteps } = useCvStepNavigation();

  const isCompleted = (index) =>
    steps[index]?.isDataFilled && visitedSteps.has(index);

  const isHighlighted = (index) =>
    index === activeStep || isCompleted(index);

  return (
    <Container>
      <SidePanel>
        {steps.map((step, index) => {
          const active = index === activeStep;
          const completed = isCompleted(index);
          const connectorLit =
            index < steps.length - 1 &&
            isHighlighted(index) &&
            isHighlighted(index + 1);

          return (
            <StepRow key={step.label} onClick={() => setActiveStep(index)}>
              <StepTextBlock>
                <Tooltip
                  title={step.tooltipText}
                  placement="left"
                  arrow
                  slotProps={tooltipStyles}
                >
                  <StepLabel className="step-label" active={active ? 1 : 0}>
                    {step.label}
                  </StepLabel>
                </Tooltip>
                <StepHelperText>{step.helperText}</StepHelperText>
              </StepTextBlock>

              <CircleColumn>
                <StepIconCircle active={active ? 1 : 0} completed={completed ? 1 : 0}>
                  {step.icon}
                </StepIconCircle>
                {index < steps.length - 1 && (
                  <ConnectorLine lit={connectorLit ? 1 : 0} />
                )}
              </CircleColumn>
            </StepRow>
          );
        })}
      </SidePanel>

      <ContentContainer>
        <ContentBox>
          <Box sx={{ mb: 3 }}>
            <CvSectionHeader
              title={steps[activeStep].label}
              subtitle={steps[activeStep].helperText}
              tooltipText={steps[activeStep].tooltipText}
              popupText={steps[activeStep].popupText}
            />
          </Box>
          {steps[activeStep].content}
        </ContentBox>
      </ContentContainer>
    </Container>
  );
};

export default CvBuilderMenu;
